'use client';
import React, { useState } from 'react';
import { NavListItem } from '@osmosis/shared/types/learndb/learnData';
import { NavListTab } from '@osmosis/shared/types/learndb/learnData';

import LearnSidenavHeader from './LearnSidenavHeader';
import PlaylistNavList from './PlaylistNavList';
import { usePagesContext } from '../../../context/PagesProvider';
import { LearnDataVideo } from '@elsevier-health/osmosis-shared/typings';
import dynamic from 'next/dynamic';

export type Props = {
  name?: LearnDataVideo['name'];
  sidebar?: LearnDataVideo['sidebar'];
  isLoggedInView?: boolean;
  hideHeader?: boolean;
};

const AddManyToPlaylist = dynamic(() => import('./AddManyToPlaylist'));

/**
 * LearnSidenav.
 */
export default function LearnSidenav(props: Props): JSX.Element | null {
  const [selectionMode, setSelectionMode] = useState(false);
  const [_selectedTab, setSelectedTab] = useState<NavListTab>();

  const [selectedItems, setSelectedItems] = useState<NavListItem[]>([]);
  const { query } = usePagesContext();

  const {
    name = '',
    sidebar,
    isLoggedInView,
    hideHeader = false,
  } = props || {};

  if (!sidebar || !sidebar.tabs.length) return null;

  const title = sidebar.title;
  const learnTitle = name;

  const tabs = sidebar?.tabs || [];
  const selectedTab =
    _selectedTab ||
    sidebar.tabs.find(
      (t) =>
        t.title?.toLowerCase() === decodeURI(sidebar.currentTab)?.toLowerCase(),
    ) ||
    sidebar.tabs[0];

  const playlistName =
    tabs.length === 1 && query.from?.toString().startsWith('/playlist/')
      ? selectedTab?.title
      : '';

  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    const newSelectedItems: NavListItem[] = [];

    // Select all
    selectedTab?.sections.forEach((section) => {
      newSelectedItems.push(...section.items);
    });

    // Otherwise deselect all
    setSelectedItems(newSelectedItems);
  };

  const onChangeCheckedValue = (item: NavListItem, checked: boolean) => {
    let newSelectedItems: NavListItem[] = [];
    if (checked) {
      newSelectedItems.push(...selectedItems, item);
      setSelectedItems(newSelectedItems);
    } else {
      newSelectedItems = selectedItems.filter(
        (n) => n.learnNodeID !== item.learnNodeID,
      );
      setSelectedItems(newSelectedItems);
    }
  };

  const navLists = selectedTab?.sections || [];

  const handleSelectTab = (newTab: NavListTab) => {
    setSelectedTab(newTab);
  };

  const tbrTag = selectedTab?.title.includes('To be retired') ? true : false;

  return (
    <div className={'flex flex-col max-h-full'}>
      {!isLoggedInView && !hideHeader && (
        <LearnSidenavHeader
          title={title}
          tabs={tabs}
          selectedTab={selectedTab}
          onSelectTab={handleSelectTab}
        />
      )}

      {isLoggedInView && !tbrTag && !hideHeader && (
        <>
          <AddManyToPlaylist
            checked={selectionMode}
            setChecked={handleToggleSelectionMode}
            items={selectedItems}
          />

          <hr className="border-t-gray-300 dark:border-t-gray-700" />
        </>
      )}

      <PlaylistNavList
        navLists={navLists}
        playlistName={playlistName as string}
        selectionMode={selectionMode}
        title={learnTitle}
        selectedItems={selectedItems}
        selectedTab={selectedTab.title}
        onChangeCheckedValue={onChangeCheckedValue}
        classNames={{
          container: hideHeader ? 'overflow-y-auto' : '',
        }}
      />
    </div>
  );
}
